import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import logger from '../utils/logger';
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../contexts/ProfileContext";
import { useEpisodeNotifications } from "../contexts/EpisodeNotificationContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useAnalytics, usePageTracking } from "../hooks/useAnalytics";
import LoadingScreen from "../components/LoadingScreen";
import { getStoryGenerationEstimate } from "../utils/storyEstimate";
import { showToast } from "../components/Toast";
import { toast } from "react-hot-toast";
import ConfirmationModal from "../components/ConfirmationModal";
import HelpFooter from "../components/HelpFooter";
import PaywallModal from "../components/PaywallModal";
import { useEntitlementGate } from "../contexts/EntitlementGateContext";
import { saveCreationDraft, takeCreationDraft } from "../utils/creationDraft";
import SubscriptionModal from "../components/SubscriptionModal";
import AnimationStylePicker from "../components/AnimationStylePicker";
import { animationStylesEnabledFor, useAnimationStyleOptions } from "../utils/animationStyles";

const STEP_LABELS = ["Characters", "Story idea", "Settings", "Title & review"];
const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

const CreateStory = () => {
  usePageTracking('Create Story');
  const { apiClient } = useAPI();
  const {
    user,
    subscriptionStatus,
    costs,
    updateUser,
    isFirstStoryUser,
    storiesCount,
    refreshSubscriptionStatus,
  } = useAuth();
  const { requireEntitlement } = useEntitlementGate();
  // Draft saved when the members-only wall interrupts creation; restored on
  // return from checkout (success_url brings the user back to this page).
  const creationDraftRef = useRef(takeCreationDraft("create-story"));

  useEffect(() => {
    const d = creationDraftRef.current;
    if (!d) return;
    if (d.storyTitle) setStoryTitle(d.storyTitle);
    if (d.storyDescription) setStoryDescription(d.storyDescription);
    if (typeof d.isPublic === "boolean") setIsPublic(d.isPublic);
    if (d.targetDuration) setTargetDuration(d.targetDuration);
    if (d.animationStyle) setAnimationStyle(d.animationStyle);
    // Land back on the SAME wizard step the wall interrupted — restoring the
    // content but resetting to step 1 read as "start over" (intern report).
    if (typeof d.currentStep === "number") setCurrentStep(d.currentStep);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { currentProfile } = useProfile();
  const { addEpisodeNotification } = useEpisodeNotifications();
  const { trackEvent, setUserProperties } = useAnalytics();
  const navigate = useNavigate();
  const location = useLocation();
  const [characters, setCharacters] = useState([]);
  const [selectedCharacters, setSelectedCharacters] = useState([]);

  // Characters load async — re-select the drafted ones when they arrive.
  // Must sit AFTER characters state is declared (avoids TDZ crash on mount).
  useEffect(() => {
    const d = creationDraftRef.current;
    if (!d?.selectedCharacterIds?.length || !characters.length) return;
    const picked = characters.filter((c) => d.selectedCharacterIds.includes(c.id));
    if (picked.length) setSelectedCharacters(picked);
    creationDraftRef.current = { ...d, selectedCharacterIds: null };
  }, [characters]); // eslint-disable-line react-hooks/exhaustive-deps

  const [storyTitle, setStoryTitle] = useState("");
  const [storyDescription, setStoryDescription] = useState("");
  const [isPublic, setIsPublic] = useState(true); // Default to public
  const [isCreating, setIsCreating] = useState(false);
  const [creationStep, setCreationStep] = useState("");
  const [loading, setLoading] = useState(true);
  const [isGeneratingStory, setIsGeneratingStory] = useState(false);
  const [storyCount, setStoryCount] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [showMicTooltip, setShowMicTooltip] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingCreation, setPendingCreation] = useState(false);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [showAllCharacters, setShowAllCharacters] = useState(false);
  const [paywallStarCost, setPaywallStarCost] = useState(0);
  const [paywallCurrentStars, setPaywallCurrentStars] = useState(0);
  const [paywallContentType, setPaywallContentType] = useState('story');
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionHighlightTier, setSubscriptionHighlightTier] = useState(null);

  // Story customization settings
  const [targetDuration, setTargetDuration] = useState(30);
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [animationStyle, setAnimationStyle] = useState('cgi');
  const stylePickerEnabled = animationStylesEnabledFor(user);
  const animationStyleOptions = useAnimationStyleOptions(apiClient, stylePickerEnabled);
  const [primaryLanguage, setPrimaryLanguage] = useState('ENGLISH');

  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [stepDirection, setStepDirection] = useState(1); // 1 forward, -1 back
  const [stepError, setStepError] = useState("");
  const [characterSearch, setCharacterSearch] = useState("");

  const validateCurrentStep = () => {
    if (currentStep === 0) {
      if (selectedCharacters.length === 0) return "Pick at least one character to continue.";
      return null;
    }
    if (currentStep === 1) {
      if (!storyDescription.trim() || storyDescription.trim().length < 10) {
        return "Tell us a bit more about your story (at least 10 characters).";
      }
      return null;
    }
    return null; // steps 2 and 3 always valid
  };

  const goNext = () => {
    const err = validateCurrentStep();
    if (err) { setStepError(err); return; }
    setStepError("");
    setStepDirection(1);
    setCurrentStep((s) => Math.min(STEP_LABELS.length - 1, s + 1));
  };

  const goBack = () => {
    setStepError("");
    setStepDirection(-1);
    setCurrentStep((s) => Math.max(0, s - 1));
  };

  // Helper function to calculate star cost based on duration (mirrors backend logic)
  const getStoryCostByDuration = (duration) => {
    // Prefer backend-provided `costs` for 30/90 sec when available.
    const cost30 = costs?.story_30_sec ?? 50;
    const cost90 = costs?.story_90_sec ?? 100;
    if (duration <= 30) return cost30;   // Quick story (30 seconds)
    if (duration <= 60) return 75;       // Short story (1 minute)
    if (duration <= 90) return cost90;   // Standard story (1.5 minutes)
    if (duration <= 180) return 300; // Long story (3 minutes) - UPDATED
    return 600;                       // Epic story (5+ minutes) - UPDATED
  };

  // Load user's characters and story count
  useEffect(() => {
    loadCharacters();
    loadStoryCount();
  }, [apiClient, user?.id, currentProfile?.id, subscriptionStatus.subscriptionType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup: Stop recording if component unmounts
  useEffect(() => {
    return () => {
      if (mediaRecorder && mediaRecorder.state !== "inactive") {
        mediaRecorder.stop();
      }
    };
  }, [mediaRecorder]);

  // Prefill when user retries from failed story card in Library
  useEffect(() => {
    const retryDescription =
      location.state?.description ?? location.state?.retryDescription;
    const retryCharacters =
      location.state?.characters ?? location.state?.retryCharacters;
    if (!retryDescription && !retryCharacters) return;

    if (retryDescription) {
      setStoryDescription(retryDescription);
    }

    if (Array.isArray(retryCharacters) && retryCharacters.length > 0) {
      const normalizedIds = retryCharacters
        .map((c) => (typeof c === "object" ? c?.id : c))
        .filter(Boolean);
      if (normalizedIds.length > 0) {
        setSelectedCharacters(normalizedIds);
      }
    }

    // Clear navigation state once consumed
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  const loadCharacters = async () => {
    if (!apiClient || !user?.id) return;

    try {
      setLoading(true);
      const response = await apiClient.getUserCharacters(user.id, currentProfile?.id);

      if (response.status) {
        setCharacters(response.data || []);
      } else {
        showToast("Failed to load characters", "error");
      }
    } catch (error) {
      logger.error("Failed to load characters:", error);
      showToast("Failed to load characters", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadStoryCount = async () => {
    if (!apiClient || !user?.id) return;

    try {
      const response = await apiClient.getUserStories();
      if (response.status && response.data) {
        setStoryCount(response.data.length);
      }
    } catch (error) {
      logger.error("Failed to load story count:", error);
    }
  };

  // Check if user is premium (STRIPE, IAP, EDDY, PREMIUM, NIKHIL, PHILIP are all premium tiers)
  const isPremium = ['STRIPE', 'IAP', 'EDDY', 'PREMIUM', 'NIKHIL', 'PHILIP', 'STARTER', 'PLUS', 'FAMILY', 'UNLIMITED'].includes(subscriptionStatus?.subscriptionType);
  const userTier = subscriptionStatus?.subscriptionType || 'NONE';

  useEffect(() => {
    logger.log("🎁 First story eligibility snapshot", {
      storiesCount,
      isFirstStoryUser,
      userTier,
    });
  }, [storiesCount, isFirstStoryUser, userTier]);

  // Tier-based feature checking
  const checkDurationAllowed = (duration) => {
    const tierLimits = {
      'NONE': 60,
      'STARTER': 90,
      'PLUS': 180,
      'FAMILY': 300,
      'UNLIMITED': 300,
      // Legacy IAP/STRIPE/PREMIUM map to PLUS (180s)
      'PREMIUM': 180,  // Changed from 300 to 180 (maps to PLUS)
      'STRIPE': 180,
      'IAP': 180,
      // Legacy special tiers (EDDY, PHILIP, NIKHIL keep full 300s access)
      'EDDY': 300,
      'PHILIP': 300,
      'NIKHIL': 300,
    };
    const maxDuration = tierLimits[userTier] || 90;
    return duration <= maxDuration;
  };

  const checkAspectRatioAllowed = (ratio) => {
    // 16:9 is free for everyone
    if (ratio === '16:9') return true;

    // 9:16 requires PLUS or higher (or legacy premium)
    if (ratio === '9:16') {
      return ['PLUS', 'FAMILY', 'UNLIMITED', 'PREMIUM', 'EDDY', 'PHILIP', 'NIKHIL', 'STRIPE', 'IAP'].includes(userTier);
    }

    return false;
  };

  const checkLanguageAllowed = (language) => {
    if (isFirstStoryUser) {
      logger.log("🌍 First story language bypass active", {
        language,
        storiesCount,
        isFirstStoryUser,
      });
      return true;
    }

    // English is free for everyone
    if (language === 'ENGLISH') return true;

    // Other languages require PLUS or higher (or legacy premium)
    return ['PLUS', 'FAMILY', 'UNLIMITED', 'PREMIUM', 'EDDY', 'PHILIP', 'NIKHIL', 'STRIPE', 'IAP'].includes(userTier);
  };

  const getRequiredTierForDuration = (duration) => {
    if (duration <= 90) return 'STARTER';   // 90s requires STARTER (free is 30/60)
    if (duration <= 180) return 'PLUS';      // 180s requires PLUS
    return 'FAMILY';                          // 300s requires FAMILY+
  };

  // Handle duration change with tier-based check
  const handleDurationChange = (e) => {
    const duration = parseInt(e.target.value);

    if (checkDurationAllowed(duration)) {
      setTargetDuration(duration);
    } else {
      // Show upgrade modal with tier-specific message
      e.target.value = targetDuration;
      const requiredTier = getRequiredTierForDuration(duration);
      setPaywallContentType(`upgrade_${requiredTier.toLowerCase()}`);
      setShowPaywallModal(true);
    }
  };

  // Handle duration change for custom dropdown (no event object)
  const handleDurationSelect = (duration) => {
    if (checkDurationAllowed(duration)) {
      setTargetDuration(duration);
    } else {
      // Show subscription modal directly with highlighted tier
      const requiredTier = getRequiredTierForDuration(duration);
      const tierMap = {
        'STARTER': 'starter',
        'PLUS': 'plus',
        'FAMILY': 'family'
      };
      setSubscriptionHighlightTier(tierMap[requiredTier] || 'plus');
      setShowSubscriptionModal(true);
    }
  };

  // Handle aspect ratio change with tier check
  const handleAspectRatioChange = (ratio) => {
    // Only allow 16:9 and 9:16 (1:1 is not supported by Veo)
    if (ratio !== '16:9' && ratio !== '9:16') {
      return;
    }

    if (checkAspectRatioAllowed(ratio)) {
      setAspectRatio(ratio);
    } else {
      // Show subscription modal directly - aspect ratio requires Plus+
      setSubscriptionHighlightTier('plus');
      setShowSubscriptionModal(true);
    }
  };

  // Handle language change with tier check
  const handleLanguageChange = (e) => {
    const language = e.target.value;
    const isAllowed = checkLanguageAllowed(language);
    logger.log("🗣️ Language selection event", {
      selectedLanguage: language,
      isAllowed,
      isFirstStoryUser,
      storiesCount,
      userTier,
      paywallBypassed: isFirstStoryUser && isAllowed,
    });

    if (isAllowed) {
      setPrimaryLanguage(language);
    } else {
      // Show subscription modal directly - languages require Plus+
      e.target.value = primaryLanguage;
      setSubscriptionHighlightTier('plus');
      setShowSubscriptionModal(true);
    }
  };

  const handleCharacterToggle = (characterId) => {
    // If character is already selected, deselect it
    if (selectedCharacters.includes(characterId)) {
      setSelectedCharacters(selectedCharacters.filter(id => id !== characterId));
      return;
    }

    // Check if maximum limit reached
    if (selectedCharacters.length >= 3) {
      return;
    }

    // Add character to selection
    setSelectedCharacters([...selectedCharacters, characterId]);
  };

  const handleGenerateStory = async () => {
    if (selectedCharacters.length === 0) {
      showToast("Please select at least one character", "error");
      return;
    }

    try {
      setIsGeneratingStory(true);
      logger.log("🤖 Generating story for characters:", selectedCharacters);

      const response = await apiClient.generateStoryIdeas(selectedCharacters);

      logger.log("📝 Story generation response:", response);

      if (response.status && response.data) {
        let generatedStory = "";

        if (response.data.stories && response.data.stories.length > 0) {
          generatedStory =
            response.data.stories[0].description || response.data.stories[0];
        } else if (response.data.story) {
          generatedStory = response.data.story;
        } else if (response.data.description) {
          generatedStory = response.data.description;
        } else if (typeof response.data === "string") {
          generatedStory = response.data;
        }

        if (generatedStory) {
          setStoryDescription(generatedStory);
          showToast("Story generated successfully!", "success");
        } else {
          logger.log("⚠️ Unexpected response format:", response.data);
          showToast(
            "Generated story but format was unexpected. Please try again.",
            "warning"
          );
        }
      } else {
        logger.log("❌ Story generation failed:", response);
        showToast(
          response.error || "Failed to generate story. Please try again.",
          "error"
        );
      }
    } catch (error) {
      logger.error("❌ Story generation error:", error);
      showToast("Failed to generate story. Please try again.", "error");
    } finally {
      setIsGeneratingStory(false);
    }
  };

  const handleVoiceToText = async () => {
    try {
      // If already recording, stop recording and transcribe
      if (isRecording && mediaRecorder) {
        mediaRecorder.stop();
        setIsRecording(false);
        return;
      }

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = async () => {
        // Stop all tracks to release microphone
        stream.getTracks().forEach((track) => track.stop());

        if (chunks.length === 0) {
          showToast("⚠️ No audio recorded. Please try again.", "error");
          return;
        }

        // Create blob from audio chunks
        const audioBlob = new Blob(chunks, { type: "audio/webm" });

        const token = apiClient.getAuthToken();

        try {
          setIsTranscribing(true);
          
          // Step 1: Transcribe audio
          showToast("Transcribing your voice...", "info");
          
          const formData = new FormData();
          formData.append("audio_file", audioBlob, "recording.webm");

          const transcribeResponse = await fetch(
            `${apiClient.baseURL}/transcribeAudio`,
            {
              method: "POST",
              headers: {
                ...(token && { Authorization: `Bearer ${token}` }),
              },
              body: formData,
            }
          );

          if (!transcribeResponse.ok) {
            throw new Error(`HTTP ${transcribeResponse.status}: ${transcribeResponse.statusText}`);
          }

          const transcribeData = await transcribeResponse.json();

          if (!transcribeData.text) {
            throw new Error("No transcription text in response");
          }

          const transcribedText = transcribeData.text.trim();

          // Step 2: Polish the transcribed text
          showToast("Polishing your story...", "info");

          // Get selected character names
          const selectedCharacterNames = characters
            .filter(c => selectedCharacters.includes(c.id))
            .map(c => c.name);

          const polishResponse = await fetch(
            `${apiClient.baseURL}/polishText`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...(token && { Authorization: `Bearer ${token}` }),
              },
              body: JSON.stringify({ 
                text: transcribedText,
                characters: selectedCharacterNames
              }),
            }
          );

          if (!polishResponse.ok) {
            throw new Error(`HTTP ${polishResponse.status}: ${polishResponse.statusText}`);
          }

          const polishData = await polishResponse.json();

          // Replace story description with polished text
          if (polishData.text || polishData.polished_text) {
            let polished = polishData.polished_text || polishData.text || "";
            
            // Remove any "Title:" or similar lines if present
            polished = polished.replace(/^Title:.*$/gm, "").trim();
            
            setStoryDescription(polished);
            showToast("Story polished successfully!", "success");
          } else {
            // Fallback: use transcribed text if polish response doesn't have expected format
            setStoryDescription(transcribedText);
            showToast("Voice transcribed successfully!", "success");
          }
        } catch (error) {
          logger.error("❌ Transcription/Polishing error:", error);
          showToast("Something went wrong, please try again.", "error");
        } finally {
          setIsTranscribing(false);
        }
      };

      // Start recording
      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error) {
      logger.error("❌ Microphone access error:", error);
      if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
        showToast("⚠️ Microphone permission denied. Please allow microphone access.", "error");
      } else {
        showToast("⚠️ Could not access microphone. Please try again.", "error");
      }
      setIsRecording(false);
    }
  };

  const dispatchStoryFailure = (storyId) => {
    window.dispatchEvent(
      new CustomEvent("story-generation-failed", {
        detail: { storyId },
      })
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (selectedCharacters.length === 0) {
      showToast("Please select at least 1 character", "error");
      return;
    }

    if (storyDescription.trim().length === 0) {
      showToast("Please enter a story description", "error");
      return;
    }

    if (!user?.id) {
      showToast("User authentication required. Please sign in again.", "error");
      return;
    }

    let creationToastId;

    try {
      trackEvent('story_creation_started', {
        character_count: selectedCharacters.length,
        has_description: !!storyDescription.trim(),
        target_duration: targetDuration,
        animation_style: animationStyle,
      });
      setIsCreating(true);

      // Members-only gate first (no-op pre-cutover): unentitled users get the
      // TrialWall, entitled users fall through to the star check as before.
      if (!requireEntitlement({ trigger: "creation", contentType: "story" })) {
        saveCreationDraft("create-story", {
          selectedCharacterIds: selectedCharacters.map((c) => c.id),
          storyTitle,
          storyDescription,
          isPublic,
          targetDuration,
          animationStyle,
          currentStep,
        });
        setIsCreating(false);
        return;
      }

      // Check stars; show paywall if short, otherwise proceed directly.
      // (The wizard's Title & Review step already recaps the story and shows
      //  the star cost on the Create button — no separate confirmation modal.)
      const starCost = getStoryCostByDuration(targetDuration);
      if (starCost && starCost > 0) {
        const currentStars = user?.pixley_stars ?? 0;

        if (currentStars < starCost) {
          setIsCreating(false);
          setPaywallStarCost(starCost);
          setPaywallCurrentStars(currentStars);
          setShowPaywallModal(true);
          return;
        }
      }

      await proceedWithStoryCreation();
    } catch (error) {
      logger.error('Error in handleSubmit:', error);
      setIsCreating(false);
      setPendingCreation(false);
      setCreationStep("");
    }
  };

  const proceedWithStoryCreation = async () => {
    let creationToastId;
    
    try {
      setIsCreating(true);
      setShowConfirmModal(false);

      // No pre-deduction here: the backend deducts stars inside /createStory and
      // returns the new balance (remaining_stars), which we apply after creation.
      // The paywall gate in handleSubmit already blocked insufficient-balance cases.

      // Capture all values into local variables BEFORE navigating,
      // so the background closure survives component unmount.
      const _apiClient = apiClient;
      const _userId = user.id;
      const _profileId = currentProfile?.id || null;
      const _storyTitle = storyTitle.trim();
      const _storyDescription = storyDescription.trim();
      const _selectedCharacters = [...selectedCharacters];
      const _characters = characters.filter((c) => _selectedCharacters.includes(c.id));
      const _isPublic = isPublic;
      const _isPremium = isPremium;
      const _targetDuration = targetDuration;
      const _aspectRatio = aspectRatio;
      const _animationStyle = animationStyle;
      const _primaryLanguage = primaryLanguage;
      const _storyCount = storyCount;

      // Navigate immediately — all generation happens in background
      toast.success(`Story submitted! Usually ready in ${getStoryGenerationEstimate(targetDuration)}. We'll notify you when it's done.`);
      sessionStorage.setItem('showStorySuggestion', 'true');
      setIsCreating(false);
      navigate("/storyverse");

      // ── Background: title generation, cover creation, story API call ──
      (async () => {
        try {
          // Step 1: Generate AI title if no name provided
          let finalStoryTitle = _storyTitle;
          if (!finalStoryTitle && _characters.length > 0) {
            logger.log("🤖 Generating AI title...");
            const firstCharacter = _characters[0];
            if (firstCharacter) {
              try {
                const titleResponse = await _apiClient.generateTitle(
                  firstCharacter.name
                );
                if (titleResponse.status) {
                  finalStoryTitle = titleResponse.title;
                  logger.log("✨ Generated title:", finalStoryTitle);
                } else {
                  finalStoryTitle = "My Amazing Story";
                }
              } catch (error) {
                logger.log("⚠️ Title generation failed, using fallback");
                finalStoryTitle = "My Amazing Story";
              }
            } else {
              finalStoryTitle = "My Amazing Story";
            }
          } else if (!finalStoryTitle) {
            finalStoryTitle = "My Amazing Story";
          }

          // Cover is now generated server-side; we no longer call /createShowCover
          // (backend returns 410 Gone — endpoint deprecated).

          // Step 2: Create the story
          const storyData = {
            user_id: _userId,
            ...(_profileId ? { profile_id: _profileId } : {}),
            title: finalStoryTitle,
            description: _storyDescription,
            characters: _selectedCharacters,
            is_public: _isPublic,
            status: "requested",
          };

          logger.log("📤 Creating story with data:", storyData);

          const finalTargetDuration = _targetDuration;

          const response = await _apiClient.createStory(
            storyData,
            finalTargetDuration,
            _aspectRatio,
            _primaryLanguage,
            _animationStyle
          );

          // Backend deducted stars inside /createStory; apply the returned balance.
          if (response?.remaining_stars !== undefined) {
            updateUser({ ...user, pixley_stars: response.remaining_stars });
          } else if (response?.data?.remaining_stars !== undefined) {
            updateUser({ ...user, pixley_stars: response.data.remaining_stars });
          }

          if (!response || response.success === false || response.error) {
            if (response?.status === "SAFETY_BLOCK" || response?.error?.includes("safety guidelines")) {
              showToast("This story description didn't meet our safety guidelines. Stars have been refunded.", "error");
            } else {
              showToast("Story creation failed. Stars have been refunded.", "error");
            }
            return;
          }

          if (response.status) {
            const storyId = response.data?.id;

            if (storyId) {
              addEpisodeNotification(storyId);
              logger.log('✅ Story notification added for:', storyId);
            }

            // story_created fires server-side from backend (no client-side to avoid double-counting)
            setUserProperties({ total_stories: _storyCount + 1 });
            if (_storyCount === 0) setUserProperties({ activation_status: 'activated' });

            if (storyId && _apiClient && _userId) {
              const tag = `CREATESTORY_${storyId}`;
              _apiClient.addUserDataTag(_userId, tag).catch((error) => {
                logger.error(`❌ Error adding tag ${tag}:`, error);
              });
            }

            try {
              await refreshSubscriptionStatus();
              logger.log("🔁 Refreshed first-story eligibility after successful creation", {
                storiesCountBefore: _storyCount,
                wasFirstStoryUser: _storyCount === 0,
              });
            } catch (refreshError) {
              logger.warn("⚠️ Failed to refresh first-story eligibility:", refreshError);
            }

            window.dispatchEvent(new Event("stories:refresh"));
          } else {
            logger.error("❌ Story creation failed:", response);
            showToast("Story creation encountered an issue. Please check your library.", "error");
          }
        } catch (error) {
          logger.error("Background story creation error:", error);
          showToast("Story creation encountered an issue. Please check your library.", "error");
        }
      })();

    } catch (error) {
      logger.error("Error creating story:", error);
      if (creationToastId) {
        toast.dismiss(creationToastId);
      }
      toast.error("Oops! Something went wrong. Please try again.");
      setIsCreating(false);
    }
  };

  if (loading) {
    return (
      <LoadingScreen message="Loading your characters..." type="character" />
    );
  }

  if (isCreating) {
    return (
      <LoadingScreen
        message={creationStep || "Creating your story..."}
        type="story"
      />
    );
  }

  // ─────────────────── Wizard step renderers ───────────────────
  const renderStepCharacters = () => {
    // Filter by search query first, then collapse to 3 rows (12 cards at 4 cols).
    const COLLAPSE_LIMIT = 12;
    const q = characterSearch.trim().toLowerCase();
    const filteredChars = q
      ? characters.filter((c) => (c.name || "").toLowerCase().includes(q))
      : characters;
    const overLimit = filteredChars.length > COLLAPSE_LIMIT;
    const visibleChars = overLimit && !showAllCharacters
      ? filteredChars.slice(0, COLLAPSE_LIMIT)
      : filteredChars;

    return (
      <div>
        <div className="flex items-center justify-between mb-1">
          <h2 style={{ ...FREDOKA, color: '#0B1A33', fontWeight: 700, fontSize: 22, letterSpacing: '-0.01em', textTransform: 'uppercase', margin: 0 }}>
            Pick your friends
          </h2>
          <span style={{ ...FREDOKA, color: '#0065F8', fontWeight: 800, fontSize: 13, letterSpacing: '0.04em', background: 'rgba(0,101,248,0.10)', padding: '4px 10px', borderRadius: 999 }}>
            {selectedCharacters.length} / 3
          </span>
        </div>
        <p style={{ ...FREDOKA, color: '#5A6B85', fontSize: 14, margin: '0 0 16px' }}>
          Choose 1–3 characters for your story.
        </p>

        {/* Search bar — only show if user has more than a handful to scan */}
        {characters.length > 6 && (
          <div
            className="relative mb-4"
            style={{
              background: '#FFFFFF',
              border: '2px solid rgba(11,26,51,0.08)',
              borderRadius: 14,
              boxShadow: '0px 2px 0px rgba(11,26,51,0.04)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 14px',
            }}
          >
            <svg width="18" height="18" fill="none" stroke="#5A6B85" strokeWidth="2.4" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path strokeLinecap="round" d="m20 20-3.5-3.5" />
            </svg>
            <input
              type="text"
              value={characterSearch}
              onChange={(e) => setCharacterSearch(e.target.value)}
              placeholder="Search characters by name…"
              className="flex-1 bg-transparent focus:outline-none"
              style={{ ...FREDOKA, color: '#0B1A33', fontSize: 14 }}
            />
            {characterSearch && (
              <button
                type="button"
                onClick={() => setCharacterSearch("")}
                aria-label="Clear search"
                className="transition-colors"
                style={{ color: '#5A6B85', padding: 2, cursor: 'pointer' }}
              >
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            )}
          </div>
        )}

        {characters.length === 0 ? (
          <div className="text-center py-12">
            <p style={{ ...FREDOKA, color: '#0B1A33', fontWeight: 700, fontSize: 20, margin: '0 0 8px' }}>No characters yet</p>
            <p style={{ ...FREDOKA, color: '#5A6B85', fontSize: 14, margin: '0 0 20px' }}>You need to create a character before starting a story.</p>
            <button
              type="button"
              onClick={() => navigate('/character-lab')}
              className="inline-flex items-center gap-2 px-6 py-3 transition-all active:scale-[0.98]"
              style={{
                ...FREDOKA,
                background: '#0065F8',
                color: '#FFFFFF',
                fontWeight: 700,
                fontSize: 14,
                border: 'none',
                borderRadius: 999,
                boxShadow: '0px 4px 0px #073B8E',
                cursor: 'pointer',
              }}
            >
              Create Your First Character
            </button>
          </div>
        ) : (
          <>
            {filteredChars.length === 0 ? (
              <div
                className="text-center py-10"
                style={{
                  background: '#FFFFFF',
                  border: '2px solid rgba(11,26,51,0.06)',
                  borderRadius: 18,
                }}
              >
                <p style={{ ...FREDOKA, color: '#0B1A33', fontWeight: 700, fontSize: 15, margin: '0 0 4px' }}>
                  No characters match "{characterSearch}"
                </p>
                <p style={{ ...FREDOKA, color: '#5A6B85', fontSize: 13, margin: 0 }}>
                  Try a different name or clear the search.
                </p>
              </div>
            ) : (
            <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
              {visibleChars.map((character) => {
                const isSelected = selectedCharacters.includes(character.id);
                const isDisabled = selectedCharacters.length >= 3 && !isSelected;
                return (
                  <button
                    key={character.id}
                    type="button"
                    onClick={() => handleCharacterToggle(character.id)}
                    disabled={isDisabled}
                    className="relative overflow-hidden transition-all"
                    style={{
                      background: '#FFFFFF',
                      border: isSelected ? '3px solid #0065F8' : '2px solid rgba(11,26,51,0.08)',
                      borderRadius: 18,
                      boxShadow: isSelected
                        ? '0px 4px 0px #073B8E, 0px 12px 28px rgba(0,101,248,0.18)'
                        : '0px 2px 0px rgba(11,26,51,0.04), 0px 4px 12px rgba(11,26,51,0.04)',
                      opacity: isDisabled ? 0.45 : 1,
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      transform: isSelected ? 'translateY(-2px)' : 'none',
                    }}
                  >
                    <div className="aspect-square overflow-hidden" style={{ background: '#EAF3FF' }}>
                      <img
                        src={character.animated_image_url || character.handdrawn_image_url || character.image_url || '/assets/image (6).png'}
                        alt={character.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = '/assets/image (6).png'; }}
                      />
                    </div>
                    <div className="px-2 py-2 text-center">
                      <p style={{ ...FREDOKA, color: '#0B1A33', fontWeight: 700, fontSize: 13, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {character.name}
                      </p>
                    </div>
                    {isSelected && (
                      <div
                        style={{
                          position: 'absolute', top: 8, right: 8,
                          width: 28, height: 28, borderRadius: '50%',
                          background: '#0065F8',
                          boxShadow: '0px 2px 0px #073B8E',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <svg width="16" height="16" fill="none" stroke="#fff" strokeWidth="3" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {overLimit && (
              <button
                type="button"
                onClick={() => setShowAllCharacters(!showAllCharacters)}
                className="w-full mt-4 py-2.5 transition-all"
                style={{
                  ...FREDOKA,
                  background: '#FFFFFF',
                  color: '#0065F8',
                  fontWeight: 700,
                  fontSize: 13,
                  border: '2px solid rgba(0,101,248,0.18)',
                  borderRadius: 999,
                  boxShadow: '0px 2px 0px rgba(11,26,51,0.04)',
                }}
              >
                {showAllCharacters
                  ? 'Show less'
                  : `Show all ${filteredChars.length} characters`}
              </button>
            )}
            </>
            )}
          </>
        )}
      </div>
    );
  };

  const renderStepIdea = () => (
    <div>
      <h2 style={{ ...FREDOKA, color: '#0B1A33', fontWeight: 700, fontSize: 22, letterSpacing: '-0.01em', textTransform: 'uppercase', margin: '0 0 4px' }}>
        Your story idea
      </h2>
      <p style={{ ...FREDOKA, color: '#5A6B85', fontSize: 14, margin: '0 0 16px' }}>
        Tell us what you'd like to happen — a setting, a problem, a fun twist.
      </p>

      <div
        style={{
          background: '#FFFFFF',
          border: '2px solid rgba(11,26,51,0.08)',
          borderRadius: 18,
          boxShadow: '0px 2px 0px rgba(11,26,51,0.04)',
          overflow: 'hidden',
        }}
      >
        <textarea
          value={storyDescription}
          onChange={(e) => setStoryDescription(e.target.value)}
          maxLength={7000}
          rows={6}
          placeholder="Once upon a time, in a sunny meadow, two friends discovered a glowing acorn that..."
          className="w-full p-5 bg-transparent focus:outline-none resize-none"
          style={{ ...FREDOKA, color: '#0B1A33', fontSize: 16, lineHeight: 1.5 }}
        />
        <div className="flex items-center justify-between gap-2 px-3 pb-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={handleVoiceToText}
              disabled={isTranscribing}
              className="inline-flex items-center gap-2 px-3.5 py-2 transition-all disabled:opacity-60"
              style={{
                ...FREDOKA,
                background: isRecording ? '#FF6B9A' : '#FFFFFF',
                color: isRecording ? '#FFFFFF' : '#334155',
                fontWeight: 700,
                fontSize: 13,
                border: isRecording ? 'none' : '2px solid rgba(11,26,51,0.08)',
                borderRadius: 999,
                boxShadow: isRecording ? '0px 2px 0px #C94D73' : '0px 2px 0px rgba(11,26,51,0.04)',
                cursor: 'pointer',
              }}
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8" />
              </svg>
              {isRecording ? 'Stop recording' : isTranscribing ? 'Transcribing…' : 'Voice'}
            </button>
            <button
              type="button"
              onClick={handleGenerateStory}
              disabled={isGeneratingStory || selectedCharacters.length === 0}
              className="inline-flex items-center gap-2 px-3.5 py-2 transition-all disabled:opacity-60"
              style={{
                ...FREDOKA,
                background: '#FFFFFF',
                color: '#0065F8',
                fontWeight: 700,
                fontSize: 13,
                border: '2px solid rgba(0,101,248,0.22)',
                borderRadius: 999,
                boxShadow: '0px 2px 0px rgba(11,26,51,0.06)',
                cursor: 'pointer',
              }}
            >
              <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l1.6 5.8L19.5 9.5l-5.9 1.7L12 17l-1.6-5.8L4.5 9.5l5.9-1.7L12 2z" />
              </svg>
              {isGeneratingStory ? 'Thinking…' : 'Surprise me'}
            </button>
          </div>
          <span style={{ ...FREDOKA, color: '#8CA0BC', fontSize: 12 }}>
            {storyDescription.length}/7000
          </span>
        </div>
      </div>
    </div>
  );

  const renderStepSettings = () => {
    const lengthOptions = [
      { value: 30, label: 'Quick', sub: '30s', locked: !checkDurationAllowed(30) },
      { value: 60, label: 'Short', sub: '1 min', locked: !checkDurationAllowed(60) },
      { value: 90, label: 'Standard', sub: '1.5 min', locked: !checkDurationAllowed(90) },
      { value: 180, label: 'Long', sub: '3 min', locked: !checkDurationAllowed(180) },
    ];
    const formatOptions = [
      { value: '16:9', label: '16:9', sub: 'Landscape', locked: !checkAspectRatioAllowed('16:9') },
      { value: '9:16', label: '9:16', sub: 'Vertical', locked: !checkAspectRatioAllowed('9:16') },
    ];
    const langOptions = [
      { value: 'ENGLISH', label: '🇺🇸 English', locked: !checkLanguageAllowed('ENGLISH') },
      { value: 'SPANISH', label: '🇪🇸 Spanish', locked: !checkLanguageAllowed('SPANISH') },
      { value: 'FRENCH', label: '🇫🇷 French', locked: !checkLanguageAllowed('FRENCH') },
      { value: 'GERMAN', label: '🇩🇪 German', locked: !checkLanguageAllowed('GERMAN') },
      { value: 'ITALIAN', label: '🇮🇹 Italian', locked: !checkLanguageAllowed('ITALIAN') },
      { value: 'HINDI', label: '🇮🇳 Hindi', locked: !checkLanguageAllowed('HINDI') },
      { value: 'ARABIC', label: '🇸🇦 Arabic', locked: !checkLanguageAllowed('ARABIC') },
    ];

    const sectionLabel = { ...FREDOKA, color: '#0B1A33', fontWeight: 800, fontSize: 15, margin: 0, letterSpacing: '-0.01em' };
    const sectionHint = { ...FREDOKA, color: '#5A6B85', fontWeight: 600, fontSize: 12.5, margin: 0 };
    const plusBadge = { background: '#FFD76A', color: '#0B1A33', fontSize: 9, fontWeight: 800, letterSpacing: '0.08em', padding: '2px 6px', borderRadius: 999, marginLeft: 6 };

    // Segmented control: equal-width cells that fill the row, so every
    // setting lines up with the same left/right edges as the style cards.
    const Section = ({ title, hint, children }) => (
      <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
          <p style={sectionLabel}>{title}</p>
          {hint && <p style={sectionHint}>{hint}</p>}
        </div>
        {children}
      </section>
    );

    return (
      <div>
        <h2 style={{ ...FREDOKA, color: '#0B1A33', fontWeight: 700, fontSize: 22, letterSpacing: '-0.01em', textTransform: 'uppercase', margin: '0 0 4px' }}>
          Settings
        </h2>
        <p style={{ ...FREDOKA, color: '#5A6B85', fontSize: 14, margin: '0 0 22px' }}>
          How your story looks, how long it runs, and who can see it.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
          {/* Animation style — the headline choice, one full-width row of cards */}
          {stylePickerEnabled && (
            <Section title="Animation style" hint="Pick the look of your whole story">
              <AnimationStylePicker options={animationStyleOptions} value={animationStyle} onChange={setAnimationStyle} />
            </Section>
          )}

          {/* Length — a film reel that grows with the story */}
          <Section title="Length" hint="How long the story runs">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }} className="length-grid">
              {lengthOptions.map((opt, i) => {
                const active = targetDuration === opt.value;
                const frames = [2, 3, 4, 6][i];
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleDurationSelect(opt.value)}
                    aria-pressed={active}
                    style={{
                      ...FREDOKA, cursor: 'pointer', border: 'none', textAlign: 'left',
                      borderRadius: 18, padding: '14px 14px 12px',
                      background: active ? 'linear-gradient(160deg, #0065F8 0%, #0047B3 100%)' : '#F3F7FD',
                      color: active ? '#fff' : '#0B1A33',
                      boxShadow: active ? '0 10px 22px rgba(0,101,248,0.30)' : 'inset 0 0 0 1px rgba(11,26,51,0.06)',
                      opacity: opt.locked ? 0.78 : 1,
                      transition: 'background 160ms, box-shadow 160ms, transform 160ms',
                      transform: active ? 'translateY(-2px)' : 'none',
                      display: 'flex', flexDirection: 'column', gap: 10,
                    }}
                  >
                    {/* film strip: more frames = longer story */}
                    <div style={{ display: 'flex', gap: 3, alignItems: 'center', height: 26 }}>
                      {Array.from({ length: frames }).map((_, k) => (
                        <span
                          key={k}
                          style={{
                            width: 18, height: 26, borderRadius: 4, flex: '0 0 auto',
                            background: active ? 'rgba(255,255,255,0.28)' : 'rgba(0,101,248,0.16)',
                            boxShadow: active ? 'inset 0 0 0 1.5px rgba(255,255,255,0.7)' : 'inset 0 0 0 1.5px rgba(0,101,248,0.45)',
                            position: 'relative',
                          }}
                        >
                          <span style={{ position: 'absolute', top: 3, left: 3, right: 3, height: 3, borderRadius: 2, background: active ? 'rgba(255,255,255,0.8)' : 'rgba(0,101,248,0.55)' }} />
                          <span style={{ position: 'absolute', bottom: 3, left: 3, right: 3, height: 3, borderRadius: 2, background: active ? 'rgba(255,255,255,0.8)' : 'rgba(0,101,248,0.55)' }} />
                        </span>
                      ))}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 6 }}>
                      <span style={{ fontWeight: 800, fontSize: 17 }}>{opt.label}</span>
                      <span style={{ fontWeight: 700, fontSize: 13, opacity: active ? 0.9 : 0.6 }}>{opt.sub}</span>
                    </div>
                    {opt.locked && <span style={{ ...plusBadge, marginLeft: 0, alignSelf: 'flex-start' }}>PLUS+</span>}
                  </button>
                );
              })}
            </div>
          </Section>

          {/* Format — the chosen style shown inside a TV and a phone */}
          <Section title="Format" hint="Where it will be watched">
            {(() => {
              const preview = (animationStyleOptions.find((o) => o.id === animationStyle) || animationStyleOptions[0] || {}).preview_url;
              const Screen = ({ ratio, w, h, radius }) => (
                <div style={{ width: w, height: h, borderRadius: radius, overflow: 'hidden', background: '#0B1A33', boxShadow: '0 0 0 3px #0B1A33, 0 8px 18px rgba(11,26,51,0.25)', position: 'relative', flex: '0 0 auto' }}>
                  {preview && <img src={preview} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
                  {ratio === '9:16' && <span style={{ position: 'absolute', top: 4, left: '50%', transform: 'translateX(-50%)', width: 22, height: 5, borderRadius: 3, background: '#0B1A33' }} />}
                </div>
              );
              return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
                  {formatOptions.map((opt) => {
                    const active = aspectRatio === opt.value;
                    const wide = opt.value === '16:9';
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleAspectRatioChange(opt.value)}
                        aria-pressed={active}
                        style={{
                          ...FREDOKA, cursor: 'pointer', border: 'none', textAlign: 'left',
                          borderRadius: 18, padding: 14, minHeight: 124,
                          background: active ? 'linear-gradient(160deg, #0065F8 0%, #0047B3 100%)' : '#F3F7FD',
                          color: active ? '#fff' : '#0B1A33',
                          boxShadow: active ? '0 10px 22px rgba(0,101,248,0.30)' : 'inset 0 0 0 1px rgba(11,26,51,0.06)',
                          opacity: opt.locked ? 0.78 : 1,
                          display: 'flex', alignItems: 'center', gap: 16,
                          transition: 'background 160ms, box-shadow 160ms, transform 160ms',
                          transform: active ? 'translateY(-2px)' : 'none',
                        }}
                      >
                        <Screen ratio={opt.value} w={wide ? 112 : 54} h={wide ? 66 : 96} radius={wide ? 8 : 12} />
                        <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <span style={{ fontWeight: 800, fontSize: 18 }}>{opt.sub}</span>
                          <span style={{ fontWeight: 700, fontSize: 13, opacity: active ? 0.9 : 0.6 }}>{wide ? 'TV, laptop, tablet' : 'Phone, Reels, Shorts'}</span>
                          {opt.locked && <span style={{ ...plusBadge, marginLeft: 0, alignSelf: 'flex-start', marginTop: 6 }}>PLUS+</span>}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
          </Section>

          {/* Language — round flag badges */}
          <Section title="Language" hint="Every character speaks it">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {langOptions.map((opt) => {
                const active = primaryLanguage === opt.value;
                const [flag, ...rest] = opt.label.split(' ');
                const name = rest.join(' ');
                return (
                  <button
                    key={opt.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => {
                      if (opt.locked) {
                        setSubscriptionHighlightTier('plus');
                        setShowSubscriptionModal(true);
                        return;
                      }
                      setPrimaryLanguage(opt.value);
                    }}
                    style={{
                      ...FREDOKA, cursor: 'pointer', border: 'none', background: 'transparent', padding: 0,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, width: 84,
                      color: active ? '#0065F8' : '#0B1A33', opacity: opt.locked ? 0.7 : 1,
                    }}
                  >
                    <span
                      style={{
                        width: 60, height: 60, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: 28,
                        background: active ? '#0065F8' : '#F3F7FD',
                        boxShadow: active ? '0 0 0 3px #fff, 0 0 0 6px #0065F8, 0 8px 18px rgba(0,101,248,0.3)' : 'inset 0 0 0 1px rgba(11,26,51,0.08)',
                        position: 'relative', transition: 'box-shadow 160ms, background 160ms',
                      }}
                    >
                      {flag}
                      {opt.locked && (
                        <span style={{ position: 'absolute', right: -4, bottom: -2, width: 22, height: 22, borderRadius: '50%', background: '#FFD76A', display: 'grid', placeItems: 'center', boxShadow: '0 0 0 2px #fff' }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#0B1A33" strokeWidth="3"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
                        </span>
                      )}
                    </span>
                    <span style={{ fontWeight: 800, fontSize: 13 }}>{name}</span>
                  </button>
                );
              })}
            </div>
          </Section>

          {/* Visibility — a locked diary vs. the Storyverse stage */}
          <Section title="Visibility" hint="Who can watch it">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
              {[
                {
                  value: false, title: 'Just us', sub: 'Only your family can see it',
                  bg: 'linear-gradient(160deg, #1B2A4A 0%, #0B1A33 100%)', idleBg: '#F3F7FD',
                  icon: (c) => (
                    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H19v18H6.5A2.5 2.5 0 0 1 4 18.5z" /><path d="M4 18.5A2.5 2.5 0 0 1 6.5 16H19" /><rect x="10" y="9" width="5" height="4" rx="1" /><path d="M11 9V8a1.5 1.5 0 0 1 3 0v1" />
                    </svg>
                  ),
                },
                {
                  value: true, title: 'On the Storyverse', sub: 'Featured for other families to enjoy',
                  bg: 'linear-gradient(160deg, #0065F8 0%, #00A3FF 100%)', idleBg: '#F3F7FD',
                  icon: (c) => (
                    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 3l2.4 5 5.6.7-4.1 3.8 1.1 5.5L12 15.3 7 18l1.1-5.5L4 8.7 9.6 8z" /><path d="M3 21h18" /><path d="M6 21l1.5-3M18 21l-1.5-3" />
                    </svg>
                  ),
                },
              ].map((opt) => {
                const active = isPublic === opt.value;
                return (
                  <button
                    key={String(opt.value)}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setIsPublic(opt.value)}
                    style={{
                      ...FREDOKA, cursor: 'pointer', border: 'none', textAlign: 'left',
                      borderRadius: 18, padding: '16px 18px', minHeight: 96,
                      background: active ? opt.bg : opt.idleBg,
                      color: active ? '#fff' : '#0B1A33',
                      boxShadow: active ? '0 10px 22px rgba(11,26,51,0.28)' : 'inset 0 0 0 1px rgba(11,26,51,0.06)',
                      display: 'flex', alignItems: 'center', gap: 14,
                      transition: 'background 160ms, box-shadow 160ms, transform 160ms',
                      transform: active ? 'translateY(-2px)' : 'none',
                      position: 'relative', overflow: 'hidden',
                    }}
                  >
                    {active && opt.value && (
                      <span aria-hidden style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 85% 20%, rgba(255,255,255,0.35), transparent 45%)' }} />
                    )}
                    <span style={{ width: 54, height: 54, borderRadius: 16, display: 'grid', placeItems: 'center', background: active ? 'rgba(255,255,255,0.18)' : 'rgba(0,101,248,0.10)', flex: '0 0 auto' }}>
                      {opt.icon(active ? '#fff' : '#0065F8')}
                    </span>
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 2, position: 'relative' }}>
                      <span style={{ fontWeight: 800, fontSize: 18 }}>{opt.title}</span>
                      <span style={{ fontWeight: 700, fontSize: 13, opacity: active ? 0.9 : 0.6 }}>{opt.sub}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </Section>
        </div>

        <style>{`
          @media (max-width: 720px) {
            .length-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          }
        `}</style>
      </div>
    );
  };

  const renderStepReview = () => {
    const selectedChars = characters.filter((c) => selectedCharacters.includes(c.id));
    const lengthLabel = (() => {
      if (targetDuration <= 30) return 'Quick (30s)';
      if (targetDuration <= 60) return 'Short (1 min)';
      if (targetDuration <= 90) return 'Standard (1.5 min)';
      if (targetDuration <= 180) return 'Long (3 min)';
      return 'Epic (5 min)';
    })();
    const langLabel = primaryLanguage.charAt(0) + primaryLanguage.slice(1).toLowerCase();

    return (
      <div>
        <h2 style={{ ...FREDOKA, color: '#0B1A33', fontWeight: 700, fontSize: 22, letterSpacing: '-0.01em', textTransform: 'uppercase', margin: '0 0 4px' }}>
          Title & review
        </h2>
        <p style={{ ...FREDOKA, color: '#5A6B85', fontSize: 14, margin: '0 0 16px' }}>
          Give your story a title (or let us pick one), then create.
        </p>

        <label style={{ ...FREDOKA, color: '#0065F8', fontWeight: 800, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
          Story title <span style={{ color: '#8CA0BC', letterSpacing: 0, textTransform: 'none', fontWeight: 600, fontSize: 11 }}>(optional)</span>
        </label>
        <input
          type="text"
          value={storyTitle}
          onChange={(e) => setStoryTitle(e.target.value)}
          placeholder="Leave blank for AI-generated title"
          maxLength={100}
          className="w-full p-3 focus:outline-none transition-all"
          style={{
            ...FREDOKA,
            background: '#FFFFFF',
            color: '#0B1A33',
            fontSize: 15,
            border: '2px solid rgba(11,26,51,0.08)',
            borderRadius: 14,
            boxShadow: '0px 2px 0px rgba(11,26,51,0.04)',
          }}
        />

        {/* Recap card */}
        <div
          className="mt-5 p-4"
          style={{
            background: 'linear-gradient(180deg, #F5FAFF 0%, #FFFFFF 100%)',
            border: '2px solid rgba(0,101,248,0.18)',
            borderRadius: 18,
            boxShadow: '0px 2px 0px rgba(11,26,51,0.04)',
          }}
        >
          <p style={{ ...FREDOKA, color: '#0065F8', fontWeight: 800, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', margin: '0 0 10px' }}>
            Recap
          </p>

          {/* Characters row */}
          <div className="flex items-center gap-3 mb-3">
            <div className="flex -space-x-3">
              {selectedChars.slice(0, 5).map((c) => (
                <img
                  key={c.id}
                  src={c.animated_image_url || c.handdrawn_image_url || c.image_url || '/assets/image (6).png'}
                  alt={c.name}
                  className="w-10 h-10 rounded-full object-cover"
                  style={{ border: '2px solid #FFFFFF', boxShadow: '0px 1px 0px rgba(11,26,51,0.06)' }}
                />
              ))}
            </div>
            <span style={{ ...FREDOKA, color: '#0B1A33', fontWeight: 600, fontSize: 14 }}>
              {selectedChars.map((c) => c.name).join(', ')}
            </span>
          </div>

          {/* Story prompt preview */}
          <p
            style={{
              ...FREDOKA,
              color: '#334155',
              fontSize: 13,
              lineHeight: 1.5,
              margin: '0 0 10px',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {storyDescription || <em style={{ color: '#8CA0BC' }}>No prompt yet</em>}
          </p>

          {/* Settings chips */}
          <div className="flex flex-wrap gap-3 justify-center">
            {[lengthLabel, aspectRatio, langLabel, isPublic ? 'Public' : 'Private'].map((chip) => (
              <span
                key={chip}
                style={{
                  ...FREDOKA,
                  background: '#FFFFFF',
                  color: '#0065F8',
                  fontWeight: 700,
                  fontSize: 11,
                  border: '2px solid rgba(0,101,248,0.18)',
                  borderRadius: 999,
                  padding: '3px 10px',
                  letterSpacing: '0.04em',
                }}
              >
                {chip}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="create-story-container min-h-screen relative"
      style={{
        background: 'linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)',
        fontFamily: "'Inter', 'Fredoka', sans-serif",
        paddingBottom: 1,
      }}
    >
      {/* Soft brand blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div style={{ position: 'absolute', top: -180, left: -160, width: 480, height: 480, borderRadius: '50%', background: 'rgba(0,101,248,0.08)', filter: 'blur(30px)' }} />
        <div style={{ position: 'absolute', bottom: -200, right: -200, width: 520, height: 520, borderRadius: '50%', background: 'rgba(0,202,255,0.08)', filter: 'blur(30px)' }} />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32 pb-16">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <button
            onClick={() => navigate("/library")}
            className="inline-flex items-center gap-2 mb-3 transition-colors"
            style={{ ...FREDOKA, color: '#5A6B85', fontSize: 14, fontWeight: 600 }}
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Stories
          </button>
          <h1
            style={{
              ...FREDOKA,
              color: '#0B1A33',
              fontWeight: 700,
              fontSize: 'clamp(1.75rem, 3.6vw, 2.6rem)',
              letterSpacing: '-0.02em',
              textTransform: 'uppercase',
              lineHeight: 1.05,
              margin: '0 0 4px',
            }}
          >
            Create <span style={{ color: '#0065F8' }}>New Story</span>
          </h1>
          <p style={{ ...FREDOKA, color: '#5A6B85', fontSize: 15, margin: 0 }}>
            Step {currentStep + 1} of {STEP_LABELS.length} · {STEP_LABELS[currentStep]}
          </p>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-6 sm:mb-8">
          {STEP_LABELS.map((label, i) => {
            const isActive = i === currentStep;
            const isDone = i < currentStep;
            return (
              <React.Fragment key={label}>
                <button
                  type="button"
                  onClick={() => { if (i < currentStep) { setStepDirection(-1); setCurrentStep(i); setStepError(""); } }}
                  className="flex items-center gap-2 transition-all"
                  style={{
                    ...FREDOKA,
                    height: 32,
                    padding: isActive ? '0 14px' : '0 10px',
                    borderRadius: 999,
                    background: isActive ? '#0065F8' : isDone ? 'rgba(0,101,248,0.15)' : 'rgba(11,26,51,0.06)',
                    color: isActive ? '#FFFFFF' : isDone ? '#0065F8' : '#5A6B85',
                    border: 'none',
                    boxShadow: isActive ? '0px 3px 0px #073B8E' : 'none',
                    fontWeight: 700,
                    fontSize: 12,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    cursor: i <= currentStep ? 'pointer' : 'default',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {isDone && (
                    <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  <span className="hidden sm:inline">{label}</span>
                  <span className="sm:hidden">{i + 1}</span>
                </button>
                {i < STEP_LABELS.length - 1 && (
                  <div style={{ flex: 1, height: 2, background: i < currentStep ? 'rgba(0,101,248,0.35)' : 'rgba(11,26,51,0.10)', borderRadius: 999 }} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step Card */}
        <form onSubmit={handleSubmit}>
          <div
            className="relative overflow-hidden"
            style={{
              minHeight: 460,
              background: '#FFFFFF',
              border: '2px solid rgba(11,26,51,0.06)',
              borderRadius: 28,
              boxShadow: '0px 4px 0px rgba(11,26,51,0.06), 0px 12px 28px rgba(11,26,51,0.08)',
              padding: '24px 24px 28px',
            }}
          >
            <AnimatePresence mode="wait" custom={stepDirection} initial={false}>
              <motion.div
                key={currentStep}
                custom={stepDirection}
                initial={(d) => ({ x: d * 40, opacity: 0 })}
                animate={{ x: 0, opacity: 1 }}
                exit={(d) => ({ x: d * -40, opacity: 0 })}
                transition={{ type: 'spring', stiffness: 300, damping: 28 }}
              >
                {currentStep === 0 && renderStepCharacters()}
                {currentStep === 1 && renderStepIdea()}
                {currentStep === 2 && renderStepSettings()}
                {currentStep === 3 && renderStepReview()}
              </motion.div>
            </AnimatePresence>
          </div>

          {stepError && (
            <div
              className="mt-3 px-4 py-2.5"
              style={{
                ...FREDOKA,
                background: 'rgba(220,38,38,0.08)',
                color: '#B91C1C',
                border: '2px solid rgba(220,38,38,0.25)',
                borderRadius: 14,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {stepError}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between mt-6 gap-3">
            {currentStep > 0 ? (
              <button
                type="button"
                onClick={goBack}
                className="inline-flex items-center gap-2 px-5 py-3 transition-all active:scale-[0.98]"
                style={{
                  ...FREDOKA,
                  background: '#FFFFFF',
                  color: '#0065F8',
                  fontWeight: 700,
                  fontSize: 15,
                  border: '2px solid rgba(0,101,248,0.22)',
                  borderRadius: 999,
                  boxShadow: '0px 3px 0px rgba(11,26,51,0.06)',
                  cursor: 'pointer',
                }}
              >
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
            ) : (
              <span />
            )}

            {currentStep < STEP_LABELS.length - 1 ? (
              <button
                key="wizard-continue"
                type="button"
                onClick={goNext}
                className="inline-flex items-center gap-2 px-6 py-3 transition-all active:scale-[0.98]"
                style={{
                  ...FREDOKA,
                  background: '#0065F8',
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: 15,
                  border: 'none',
                  borderRadius: 999,
                  boxShadow: '0px 4px 0px #073B8E, 0px 12px 28px rgba(0,101,248,0.20)',
                  cursor: 'pointer',
                }}
              >
                Continue
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <button
                key="wizard-create"
                type="submit"
                disabled={!storyDescription.trim() || selectedCharacters.length === 0}
                className="inline-flex items-center gap-2 px-6 py-3 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  ...FREDOKA,
                  background: '#0065F8',
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: 15,
                  border: 'none',
                  borderRadius: 999,
                  boxShadow: '0px 4px 0px #073B8E, 0px 14px 32px rgba(0,101,248,0.22)',
                  cursor: 'pointer',
                }}
              >
                Create My Story ✨
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 ml-1"
                  style={{
                    background: 'rgba(255,255,255,0.22)',
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 800,
                  }}
                >
                  {getStoryCostByDuration(targetDuration)}
                  <img src="/assets/pixley_stars.png" alt="" style={{ width: 12, height: 12, filter: "brightness(0) invert(1)" }} />
                </span>
              </button>
            )}
          </div>

          {currentStep === STEP_LABELS.length - 1 && (
            <p className="text-center mt-3" style={{ ...FREDOKA, color: '#8CA0BC', fontSize: 12 }}>
              Story generation typically takes 8–10 minutes. We'll notify you when it's ready ✨
            </p>
          )}
        </form>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onConfirm={proceedWithStoryCreation}
        onCancel={() => {
          setShowConfirmModal(false);
          setPendingCreation(false);
          setIsCreating(false);
          setCreationStep("");
        }}
        title="Confirm Story Creation"
        starCost={getStoryCostByDuration(targetDuration)}
        currentStars={user?.pixley_stars ?? 0}
        actionType="story"
        confirmText="Create Story"
        cancelText="Cancel"
        confirmColor="purple"
      />

      {/* Paywall Modal */}
      <PaywallModal
        isOpen={showPaywallModal}
        onClose={() => {
          setShowPaywallModal(false);
          setPaywallContentType('story');
        }}
        starCost={paywallStarCost}
        currentStars={paywallCurrentStars}
        contentType={paywallContentType}
        apiClient={apiClient}
        user={user}
      />

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => {
          setShowSubscriptionModal(false);
          setSubscriptionHighlightTier(null);
        }}
        apiClient={apiClient}
        user={user}
        highlightTier={subscriptionHighlightTier}
      />
      <HelpFooter />
    </div>
  );
};

export default CreateStory;

